import React from 'react';
import { MiniLoader } from '@pwc/pwc-deals-ui';
import { renderToStaticMarkup, renderToString } from 'react-dom/server';
import { syncedInfoWrapped } from 'common/components/Autosync/SyncedInfo.tsx';
import { isPowerPivotLibraryItem } from 'features/analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes';
import {
  setShowPowerPivotNotSupportedModal,
  setShowSharePointModalPowerPivot,
} from 'features/analyses/common/LibraryItemCard/PowerPivot/redux/powerPivotSlice';
import {
  clearClipboard,
  getExcelLibraryItemsByGroup,
  getLibraryItemsFromClipboard,
  getPptLibraryItemsByGroup,
} from '../../../features/contentLibrary/libraryItemService';
import {
  onSyncedInfoEventListener,
  removeMiniloaderFromLibraryItemStore,
} from '../../helpers/insertMinitableEventListener';

import { junctionColorMapping } from '../../helpers/junctionColorMapping';
import UnableToLoadContent from '../../../features/analyses/common/UnableToLoadContent/UnableToLoadContent';
import showAutosync from '../../helpers/ShowAutosync.ts';
import { showModalError } from '../../redux/errors/errorsSlice';
import { UNABLE_TO_ADD_OBJECT } from '../ModalError/constants';

const getUnableToLoadomponent = (type, data) => {
  return renderToStaticMarkup(<UnableToLoadContent type={type} data={data} />);
};

export const insertTablesAndCharts = (
  editor,
  container,
  projectId,
  cb,
  dispatch,
  contextStatusInfoItem,
  isPwcUser
) => {
  const editorContainer = document.getElementById(container);

  const getExcelChart = async (data, id) => {
    const { driveItemId, worksheetName, uniqueId, chartName, name } = data;

    const rangeObject = { cellRange: name };

    const request = {
      ...data,
      groupedByKey: driveItemId,
      excelLibraryItems: [
        {
          id,
          type: 300,
          data: {
            formatType: 'SVG',
            worksheetName,
            uniqueId,
            chartName,
            ...rangeObject,
          },
          childrenData: [],
        },
      ],
    };
    const item = await getExcelLibraryItemsByGroup(projectId, request);
    const { value: rendered, autoSync } = item.fileContents[0];
    return { request, rendered, autoSync, renderedSvg: rendered };
  };
  const getExcelNameRange = async (data, id, type) => {
    const { driveItemId, worksheetName, uniqueId, chartName, name } = data;

    const rangeObject =
      type === 301
        ? { namedRange: name, cellRange: data?.cellRange }
        : { cellRange: name };

    const request = {
      ...data,
      groupedByKey: driveItemId,
      excelLibraryItems: [
        {
          id,
          type,
          data: {
            formatType: 'HTML',
            worksheetName,
            uniqueId,
            chartName,
            ...rangeObject,
          },
          childrenData: [],
        },
        {
          id,
          type,
          data: {
            formatType: 'SVG',
            worksheetName,
            uniqueId,
            chartName,
            ...rangeObject,
          },
          childrenData: [],
        },
      ],
    };
    const item = await getExcelLibraryItemsByGroup(projectId, request);
    const { value: rendered, autoSync } = item.fileContents[0];
    const { value: renderedSvg } = item.fileContents[1];
    return { request, rendered, renderedSvg, autoSync };
  };

  const getPowerpointGroup = async (data, id) => {
    const { driveItemId, groupName, slideNumber } = data;
    const request = {
      ...data,
      groupedByKey: driveItemId,
      pptLibraryItems: [
        {
          id,
          type: 500,
          data: {
            formatType: 'SVG',
            groupName,
            slideNumber,
          },
          childrenData: [],
        },
      ],
    };
    const item = await getPptLibraryItemsByGroup(projectId, request);
    const { value: renderedSvg } = item.fileContents[0];
    return { request, rendered: renderedSvg };
  };

  const addLibraryItem = (html, id, item = {}) => {
    const { type: contentType, request, renderedSvg } = item;
    const libraryItem = {
      id,
      rendered: html,
      renderedHtml: html,
      renderedSvg,
      contentType,
      request,
    };
    editor.execute('addLibraryItem', libraryItem);
    return libraryItem;
  };

  const addMiniLoader = () => {
    const html = renderToString(<MiniLoader />);
    addLibraryItem(html, 'miniloader');
  };

  const removeMiniLoader = () => {
    removeMiniloaderFromLibraryItemStore();
  };

  const insertMiniTableListener = async () => {
    if (!editor || editor.state === 'destroyed') return;
    addMiniLoader();

    let response;

    try {
      response = await getLibraryItemsFromClipboard(projectId);
    } catch (e) {
      editor.execute('undo');
      removeMiniloaderFromLibraryItemStore();
      dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
      return;
    }

    if (!response) {
      dispatch(setShowSharePointModalPowerPivot(false));
      editorContainer.dispatchEvent(
        new CustomEvent(`inserting-minitable-no-data`)
      );
      editor.execute('undo');
      removeMiniLoader();
      return;
    }

    if (response) {
      let item = {};
      let autosyncString = '';

      const { id, type, data } = response;
      try {
        if (type === 300 || type === 'ExcelChart') {
          item = await getExcelChart(data, id);
        } else if (type === 301 || type === 'ExcelNamedRange') {
          item = await getExcelNameRange(data, id, 301);
        } else if (type === 302 || type === 'ExcelCellRange') {
          item = await getExcelNameRange(data, id, 302);
        } else if (type === 500 || type === 'PowerpointGroup') {
          item = await getPowerpointGroup(data, id);
        } else if (isPowerPivotLibraryItem(type)) {
          editor.execute('undo');
          removeMiniLoader();
          dispatch(setShowPowerPivotNotSupportedModal(true));
          return;
        }

        const { request, rendered, autoSync } = item;
        const mappedJunctionStlyeData = junctionColorMapping(
          rendered,
          undefined,
          item
        );

        const canShowAutoSync = await showAutosync(projectId, isPwcUser);
        if (canShowAutoSync) {
          const { worksheetName, groupName, sharePointItemUrl, slideNumber } =
            request;
          const excelPPTItem = {
            groupName,
            libraryItemId: id,
            sharePointItemUrl,
            formatType: data.formatType,
            worksheetName,
            slideNumber,
            autoSync,
          };
          autosyncString = syncedInfoWrapped({
            projectId,
            excelPPTItem,
            contextStatusInfoItem,
            isPwcUser,
          });

          document.addEventListener(
            'click',
            onSyncedInfoEventListener({
              canShowAutoSync,
              contextStatusInfoItem,
              data,
              editor,
              excelPPTItem,
              id,
              libraryItem: {
                id,
                formatType: data?.formatType,
                rendered: `${mappedJunctionStlyeData}${autosyncString}`,
                renderedHtml: `${mappedJunctionStlyeData}`,
                renderedSvg: item.renderedSvg,
                contentType: type,
                request,
                data,
                autoSync,
              },
              projectId,
              renderedHtml: mappedJunctionStlyeData,
              renderedSvg: item.renderedSvg,
              isPwcUser,
            })
          );
        }

        const html = `${mappedJunctionStlyeData}${autosyncString}`;
        const libraryItem = addLibraryItem(html, id, item);
        if (cb) {
          cb(libraryItem);
        }
        removeMiniLoader();
        // clear the clipboard
        await clearClipboard(projectId, response);
      } catch (e) {
        const html = getUnableToLoadomponent(type, data);
        removeMiniLoader();
        addLibraryItem(html, id, item);
      }
    }
  };

  editorContainer.addEventListener(
    `inserting-minitable-chart`,
    insertMiniTableListener
  );

  return () => {
    editorContainer.removeEventListener(
      `inserting-minitable-chart`,
      insertMiniTableListener
    );
  };
};
